<script setup>
import {ref, computed} from 'vue';
import {router, usePage} from '@inertiajs/vue3';
import {Head, Link} from '@inertiajs/vue3';
import JetApplicationMark from '@/Jetstream/ApplicationMark.vue';
import JetBanner from '@/Jetstream/Banner.vue';
import JetDropdown from '@/Jetstream/Dropdown.vue';
import JetDropdownLink from '@/Jetstream/DropdownLink.vue';
import JetNavLink from '@/Jetstream/NavLink.vue';
import JetResponsiveNavLink from '@/Jetstream/ResponsiveNavLink.vue';
import AlertBox from "../Components/AlertBox.vue";
import LanguageSelector from "../Components/LanguageSelector.vue";

defineProps({
    title: String,
    onBoardingActive: Boolean,
});

const showingNavigationDropdown = ref(false);

const switchToWorkspace = (workspace) => {
    router.put(route('current-workspace.update'), {
        workspace_id: workspace.id,
    }, {
        preserveState: false,
    });
};

const availableWorkspaces = computed(() => {
    if (!usePage().props.auth || !usePage().props.auth.user) {
        return [];
    }
    return usePage().props.auth.user?.workspaces;
});

const workspaceSwitchAvailable = computed(() => {
    return availableWorkspaces.value.length > 1;
});

const logout = () => {
    router.post(route('logout'));
};
</script>

<template>
    <div>
        <Head :title="title"/>

        <JetBanner/>

        <div class="min-h-screen bg-gray-100 flex flex-col">
            <nav class="bg-white border-b border-primary-400">
                <!-- Primary Navigation Menu -->
                <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div class="flex justify-between h-16">
                        <div class="flex">
                            <!-- Logo -->
                            <div class="shrink-0 flex items-center">
                                <Link :href="route('dashboard')">
                                    <JetApplicationMark class="block h-9 w-auto"/>
                                </Link>
                            </div>

                            <!-- Navigation Links -->
                            <div class="hidden space-x-8 sm:-my-px sm:ml-10 sm:flex"
                                 v-if="$page.props.auth.user.is_on_boarded">
                                <JetNavLink :href="route('dashboard')"
                                            :active="route().current('dashboard')">
                                    {{ $t('Overview') }}
                                </JetNavLink>
                                <JetNavLink :href="route('containers.index')"
                                            :active="route().current('containers.index')"
                                            v-if="!$page.props.auth.user.is_superuser">
                                    {{ $t('File Storage') }}
                                </JetNavLink>
                                <JetNavLink :href="route('consultants.index')"
                                            :active="route().current('consultants.index')"
                                            v-if="!$page.props.auth.user.is_superuser && !$page.props.auth.user.is_consultant">
                                    {{ $t('Contacts') }}
                                </JetNavLink>
                                <JetNavLink :href="route('consultant-customers.index')"
                                            :active="route().current('consultant-customers.*')"
                                            v-if="$page.props.auth.user.is_consultant">
                                    {{ $t('Customers') }}
                                </JetNavLink>
                                <JetNavLink :href="route('customers.index')"
                                            :active="route().current('customers.*')"
                                            v-if="$page.props.auth.user.is_superuser">
                                    {{ $t('Workspaces') }}
                                </JetNavLink>
                                <JetNavLink :href="route('users.index')"
                                            :active="route().current('users.*')"
                                            v-if="$page.props.auth.user.is_superuser">
                                    {{ $t('Users') }}
                                </JetNavLink>
                                <JetNavLink :href="route('importer.index')"
                                            :active="route().current('importer.*')"
                                            v-if="$page.props.auth.user.is_superuser">
                                    {{ $t('Import') }}
                                </JetNavLink>
                                <JetNavLink :href="route('container-presets.index')"
                                            :active="route().current('container-presets.*')"
                                            v-if="$page.props.auth.user.is_superuser">
                                    {{ $t('Storage Templates') }}
                                </JetNavLink>
                            </div>
                        </div>

                        <div class="hidden sm:flex sm:items-center sm:ml-6">
                            <div class="ml-3 relative">
                                <!-- Teams Dropdown -->
                                <JetDropdown
                                    v-if="workspaceSwitchAvailable"
                                    align="right" width="60">
                                    <template #trigger>
                    <span class="inline-flex rounded-md">
                        <button type="button"
                                class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 bg-white hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:bg-gray-50 active:bg-gray-50 transition">
                            {{ $page.props.auth.user.current_workspace.name }}

                            <svg
                                class="ml-2 -mr-0.5 h-4 w-4"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path fill-rule="evenodd"
                                      d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                                      clip-rule="evenodd"/>
                            </svg>
                        </button>
                    </span>
                                    </template>

                                    <template #content>
                                        <div class="w-60">
                                            <!-- Account Switcher -->
                                            <div class="block px-4 py-2 text-xs text-gray-400">
                                                {{ $t('Switch account') }}
                                            </div>

                                            <template v-for="workspace in availableWorkspaces"
                                                      :key="workspace.id">
                                                <form @submit.prevent="switchToWorkspace(workspace)">
                                                    <JetDropdownLink as="button">
                                                        <div class="flex items-center">
                                                            <svg
                                                                v-if="workspace.id == $page.props.auth.user.current_workspace_id"
                                                                class="mr-2 h-5 w-5 text-green-400"
                                                                fill="none"
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                                stroke-width="2"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path
                                                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                                            </svg>
                                                            <div>{{ workspace.name }}</div>
                                                        </div>
                                                    </JetDropdownLink>
                                                </form>
                                            </template>
                                        </div>
                                    </template>
                                </JetDropdown>
                            </div>

                            <!-- Settings Dropdown -->
                            <div class="ml-3 relative">
                                <JetDropdown align="right" width="48">
                                    <template #trigger>
                                        <button v-if="$page.props.jetstream.managesProfilePhotos"
                                                class="flex text-sm border-2 border-transparent rounded-full focus:outline-none focus:border-gray-300 transition">
                                            <img class="h-8 w-8 rounded-full object-cover"
                                                 :src="$page.props.auth.user.profile_photo_url"
                                                 :alt="$page.props.auth.user.name">
                                        </button>

                                        <span v-else class="inline-flex rounded-md">
                                            <button type="button"
                                                    class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 bg-white hover:text-gray-700 focus:outline-none transition">
                                                {{ $page.props.auth.user.name }}

                                                <svg
                                                    class="ml-2 -mr-0.5 h-4 w-4"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                >
                                                    <path fill-rule="evenodd"
                                                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                          clip-rule="evenodd"/>
                                                </svg>
                                            </button>
                                        </span>
                                    </template>

                                    <template #content>
                                        <!-- Account Management -->
                                        <div class="block px-4 py-2 text-xs text-gray-400"
                                             v-if="$page.props.auth.user.is_on_boarded">
                                            {{ $t('Manage Account') }}
                                        </div>

                                        <JetDropdownLink :href="route('profile.show')"
                                                         v-if="$page.props.auth.user.is_on_boarded">
                                            {{ $t('Profile') }}
                                        </JetDropdownLink>

                                        <JetDropdownLink v-if="$page.props.jetstream.hasApiFeatures"
                                                         :href="route('api-tokens.index')">
                                            API Tokens
                                        </JetDropdownLink>

                                        <div class="border-t border-gray-100"/>

                                        <!-- Authentication -->
                                        <form @submit.prevent="logout">
                                            <JetDropdownLink as="button">
                                                {{ $t('Log Out') }}
                                            </JetDropdownLink>
                                        </form>
                                    </template>
                                </JetDropdown>
                            </div>
                        </div>

                        <!-- Hamburger -->
                        <div class="-mr-2 flex items-center sm:hidden">
                            <button
                                class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 focus:text-gray-500 transition"
                                @click="showingNavigationDropdown = ! showingNavigationDropdown">
                                <svg
                                    class="h-6 w-6"
                                    stroke="currentColor"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        :class="{'hidden': showingNavigationDropdown, 'inline-flex': ! showingNavigationDropdown }"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M4 6h16M4 12h16M4 18h16"
                                    />
                                    <path
                                        :class="{'hidden': ! showingNavigationDropdown, 'inline-flex': showingNavigationDropdown }"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Responsive Navigation Menu -->
                <div :class="{'block': showingNavigationDropdown, 'hidden': ! showingNavigationDropdown}"
                     class="sm:hidden">
                    <div class="pt-2 pb-3 space-y-1" v-if="$page.props.auth.user.is_on_boarded">
                        <JetResponsiveNavLink :href="route('dashboard')"
                                              :active="route().current('dashboard')">
                            {{ $t('Overview') }}
                        </JetResponsiveNavLink>
                        <JetResponsiveNavLink :href="route('containers.index')"
                                              :active="route().current('containers.index')"
                                              v-if="!$page.props.auth.user.is_superuser">
                            {{ $t('File Storage') }}
                        </JetResponsiveNavLink>
                        <JetResponsiveNavLink :href="route('consultants.index')"
                                              :active="route().current('consultants.index')"
                                              v-if="!$page.props.auth.user.is_superuser && !$page.props.auth.user.is_consultant">
                            {{ $t('Contacts') }}
                        </JetResponsiveNavLink>
                        <JetResponsiveNavLink :href="route('consultant-customers.index')"
                                              :active="route().current('consultant-customers.*')"
                                              v-if="$page.props.auth.user.is_consultant">
                            {{ $t('Customers') }}
                        </JetResponsiveNavLink>
                        <JetResponsiveNavLink :href="route('customers.index')"
                                              :active="route().current('customers.*')"
                                              v-if="$page.props.auth.user.is_superuser">
                            {{ $t('Workspaces') }}
                        </JetResponsiveNavLink>
                        <JetResponsiveNavLink :href="route('users.index')"
                                              :active="route().current('users.*')"
                                              v-if="$page.props.auth.user.is_superuser">
                            {{ $t('Users') }}
                        </JetResponsiveNavLink>
                        <JetResponsiveNavLink :href="route('importer.index')"
                                              :active="route().current('importer.*')"
                                              v-if="$page.props.auth.user.is_superuser">
                            {{ $t('Import') }}
                        </JetResponsiveNavLink>
                        <JetResponsiveNavLink :href="route('container-presets.index')"
                                              :active="route().current('container-presets.*')"
                                              v-if="$page.props.auth.user.is_superuser">
                            {{ $t('Storage Templates') }}
                        </JetResponsiveNavLink>
                    </div>

                    <!-- Responsive Settings Options -->
                    <div class="pt-4 pb-1 border-t border-gray-200">
                        <div class="flex items-center px-4">
                            <div v-if="$page.props.jetstream.managesProfilePhotos" class="shrink-0 mr-3">
                                <img class="h-10 w-10 rounded-full object-cover"
                                     :src="$page.props.auth.user.profile_photo_url" :alt="$page.props.auth.user.name">
                            </div>

                            <div>
                                <div class="font-medium text-base text-gray-800">
                                    {{ $page.props.auth.user.name }}
                                </div>
                                <div class="font-medium text-sm text-gray-500">
                                    {{ $page.props.auth.user.email }}
                                </div>
                            </div>
                        </div>

                        <div class="mt-3 space-y-1">
                            <JetResponsiveNavLink :href="route('profile.show')"
                                                  :active="route().current('profile.show')"
                                                  v-if="$page.props.auth.user.is_on_boarded">
                                {{ $t('Profile') }}
                            </JetResponsiveNavLink>

                            <JetResponsiveNavLink v-if="$page.props.jetstream.hasApiFeatures"
                                                  :href="route('api-tokens.index')"
                                                  :active="route().current('api-tokens.index')">
                                API Tokens
                            </JetResponsiveNavLink>

                            <!-- Authentication -->
                            <form method="POST" @submit.prevent="logout">
                                <JetResponsiveNavLink as="button">
                                    {{ $t('Log Out') }}
                                </JetResponsiveNavLink>
                            </form>

                            <!-- Team Management -->
                            <template v-if="workspaceSwitchAvailable">
                                <div class="border-t border-gray-200"/>

                                <!-- Account Switcher -->
                                <div class="block px-4 py-2 text-xs text-gray-400">
                                    {{ $t('Switch account') }}
                                </div>

                                <template v-for="workspace in availableWorkspaces" :key="workspace.id">
                                    <form @submit.prevent="switchToWorkspace(workspace)">
                                        <JetResponsiveNavLink as="button">
                                            <div class="flex items-center">
                                                <svg
                                                    v-if="workspace.id == $page.props.auth.user.current_workspace_id"
                                                    class="mr-2 h-5 w-5 text-green-400"
                                                    fill="none"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                    stroke-width="2"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                                </svg>
                                                <div>{{ workspace.name }}</div>
                                            </div>
                                        </JetResponsiveNavLink>
                                    </form>
                                </template>
                            </template>
                        </div>
                    </div>
                </div>
            </nav>

            <!-- Page Heading -->
            <!--            <header v-if="$slots.header" class="bg-white shadow">
                            <div class="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                                <slot name="header" />
                            </div>
                        </header>-->

            <!-- Page Content -->
            <main class="py-8">
                <template v-if="!$page.props.auth.user?.two_factor_enabled && !onBoardingActive">
                    <template v-if="$page.props.auth.user.requires_mfa">
                        <AlertBox type="danger" class="max-w-6xl mx-auto mb-6">
                            <div class="flex-1 md:flex md:justify-between">
                                <p>
                                    {{ $t('Two-factor authentication is mandatory for consultants and administrators.') }}<br/>{{ $t('Please activate two-factor authentication to be able to use the portal.') }}
                                </p>
                            </div>
                        </AlertBox>
                    </template>
                    <template v-else>
                        <AlertBox type="warning" class="max-w-6xl mx-auto mb-6">
                            <div class="flex-1 md:flex md:justify-between">
                                <p>
                                    <strong>{{ $t('Two-factor authentication is currently not enabled for your account.') }}</strong><br>{{ $t('Please') }}
                                    <Link :href="route('profile.show')" class="font-bold underline">{{ $t('activate two-factor authentication') }}
                                    </Link>
                                    {{ $t('to improve the security of your data.') }}
                                </p>
                            </div>
                        </AlertBox>
                    </template>

                </template>

                <slot/>
            </main>

            <footer class="mt-auto bg-white border-t border-primary-400 text-gray-500">
                <div
                    class="max-w-7xl mx-auto sm:px-6 lg:px-8 py-3 flex flex-col justify-center items-center md:flex-row md:justify-between">
                    <div class="mb-2 md:mb-0">
                        <LanguageSelector class="py-0"></LanguageSelector>
                    </div>
                    <div>
                        <div>
                            {{ $t('HQAM - Customer Portal') }}
                        </div>
                        <div class="text-center md:text-left">
                            <a href="mailto:info@hqam.ch" class="text-primary-500 hover:underline">info@hqam.ch</a>
                            <span class="hidden md:inline"> / </span>
                            <span class="md:hidden"><br/></span>
                            <a href="tel:+41432223141" class="text-primary-500 hover:underline">+41 (0)-43 222 31 41</a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    </div>
</template>
